import { DestroyRef, Directive, Host, Input, OnInit, Optional, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store, createSelector } from '@ngrx/store';
import { QuillEditorComponent } from 'ngx-quill';
import Quill, { Delta, EmitterSource } from 'quill';

import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';

import { QuillTemplate, QuillTemplateLocation } from '../../generated/graphql.generated';
import { TEMPLATE_VARIABLE_DATA } from '../shared/quill-modules/quill-template-variable-data.registry';
import { TEMPLATE_VARIABLES } from '../shared/quill-modules/quill-templates.helpers';
import { TemplateToolbarQuillModule } from '../shared/quill-modules/templateToolbar.quillModule';
import { VariableToolbarQuillModule } from '../shared/quill-modules/variableToolbar.quillModule';

import { brandingFeature } from '../state/branding.store';

interface OnDefaultTemplateSetEvent {
    source: EmitterSource;
    content: Delta
    text: string;
}
@Directive({
  selector: '[appQuillTemplateVariableToolbar]',
  standalone: true,
})
export class QuillTemplateVariableToolbarDirective implements OnInit {

  private _templatesLocations$ = new BehaviorSubject<QuillTemplateLocation[] | null>(null);

  private templatesLocations$ = this._templatesLocations$.asObservable().pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  );

  @Input({ required: true }) set templatesLocations(value: QuillTemplateLocation[]) {
    this._templatesLocations$.next(value);
  };

  @Input() editorId?: string;
  @Input({ required: true }) variablesLocation: QuillTemplateLocation;
  @Input() setDefaultTemplate: boolean = false;
  @Output() defaultTemplateSetCallback?: (event: OnDefaultTemplateSetEvent) => void;
  @Input() isSubject?: boolean = false;

  private defaultTemplateSet = false;

  constructor(
    @Host() @Optional() private quillEditorComponent: QuillEditorComponent,
    private store: Store,
    private destroyRef: DestroyRef,
  ) {}

  private setDefaultTemplateIfNeeded(quill: Quill, templates: QuillTemplate[]) {
    if(this.defaultTemplateSet) return;

    const defaultTemplateAccessor = this.isSubject ? 'subjectContents' : 'contents';
    const editorContents = quill.getText().trim();
    const isEditorEmpty = !(editorContents.length > 0);
    if (!this.setDefaultTemplate || !isEditorEmpty) return;

    const defaultTemplate = templates.find((t: any) => t.isDefault);
    if (
      this.setDefaultTemplate &&
      defaultTemplate?.[defaultTemplateAccessor] &&
      templates &&
      templates.length > 0 &&
      isEditorEmpty
    ) {
      try {
        const delta = JSON.parse(defaultTemplate[defaultTemplateAccessor]);
        quill.setContents(delta, 'user');
        this.defaultTemplateSet = true;
        if (this.defaultTemplateSetCallback) {
          const templateEvent: OnDefaultTemplateSetEvent = {
            source: 'silent',
            content: delta,
            text: quill.getText(),
          };
          this.defaultTemplateSetCallback(templateEvent);
        }
      } catch (e) {
          console.error('Error setting default template:', e);
        }
    }
  }

  ngOnInit() {
    this.quillEditorComponent?.onEditorCreated
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((quill: Quill) => this.templatesLocations$.pipe(
          filter(Boolean),
          switchMap((templateLocations) => {
            const selectLocationTemplates = this.store.select(createSelector(
              brandingFeature.selectTemplates,
              (templates) => templates.filter((t) => templateLocations.includes(t.location)),
            ));

            const dataSelector = TEMPLATE_VARIABLE_DATA[this.variablesLocation];
            const variableData$ = dataSelector ? this.store.select(dataSelector) : of({});

            // Join templates with the resolved data selector so a live data
            // change (e.g. the dispatch user picking a different recipient)
            // re-pushes to the toolbar, not just a templatesLocations change.
            return combineLatest([selectLocationTemplates, variableData$]).pipe(
              tap(([ templates, variableData ]) => {

                const variableToolbar = quill.getModule('variableToolbar') as VariableToolbarQuillModule;

                if (variableToolbar ) {
                  variableToolbar.updateVariables(TEMPLATE_VARIABLES[this.variablesLocation]);
                  variableToolbar.updateData(variableData);
                }

                const templateToolbar = quill.getModule('templateToolbar') as TemplateToolbarQuillModule;

                if (templateToolbar) {
                  templateToolbar.updateTemplates(templates);
                }

                this.setDefaultTemplateIfNeeded(quill, templates);
              }),
            );
          }),
        )),
      ).subscribe();
  }
}
